import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Loader2, AlertCircle, CreditCard, Calendar, DollarSign } from 'lucide-react';
import { useLocale } from '../context/LocaleContext';

const PaymentHistoryModal = ({ isOpen, onClose }) => {
  const { t } = useLocale();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadPaymentHistory();
    }
  }, [isOpen]);

  const loadPaymentHistory = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/history`, {
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please log in again.');
        } else if (response.status === 404) {
          // No payments endpoint or no data - show empty state instead of error
          setPayments([]);
          setLoading(false);
          return;
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to load payment history');
        }
      }

      const data = await response.json();
      setPayments(data.payments || []);
    } catch (err) {
      console.error('Payment history error:', err);
      // For network errors, show empty state instead of error
      if (err.message.includes('Failed to fetch') || err.message.includes('Network')) {
        setPayments([]);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'succeeded': { variant: 'default', className: 'bg-green-500 text-white', label: 'Completed' },
      'pending': { variant: 'secondary', className: 'bg-yellow-500 text-white', label: 'Pending' },
      'failed': { variant: 'destructive', className: 'bg-red-500 text-white', label: 'Failed' },
      'refunded': { variant: 'outline', className: 'text-gray-600', label: 'Refunded' }
    };

    const config = statusConfig[status] || statusConfig['pending'];
    return (
      <Badge variant={config.variant} className={config.className}>
        {config.label}
      </Badge>
    );
  };

  const getPaymentType = (description) => {
    if (description?.toLowerCase().includes('subscription') || description?.toLowerCase().includes('pro')) {
      return 'Subscription';
    } else if (description?.toLowerCase().includes('credit')) {
      return 'Credits Purchase';
    }
    return 'Payment';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[650px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('health.paymentHistory')}</DialogTitle>
          <DialogDescription>
            {t('health.viewTransactions')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {!loading && !error && payments.length === 0 && (
            <div className="text-center py-12">
              <CreditCard className="mx-auto h-12 w-12 text-gray-400 mb-3" />
              <p className="text-sm text-gray-500">No payment history yet</p>
              <p className="text-xs text-gray-400 mt-1">
                Your transactions will appear here after your first purchase
              </p>
            </div>
          )}

          {!loading && !error && payments.length > 0 && (
            <div className="space-y-3">
              {payments.map((payment, index) => (
                <div
                  key={payment.id || index}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <CreditCard className="h-4 w-4 text-gray-500" />
                        <h4 className="font-semibold text-sm text-gray-900">
                          {getPaymentType(payment.description)}
                        </h4>
                        {getStatusBadge(payment.status)}
                      </div>
                      
                      <p className="text-xs text-gray-600 mb-2">
                        {payment.description || 'No description'}
                      </p>
                      
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          <span>{formatDate(payment.createdAt || payment.created_at)}</span>
                        </div>
                        
                        {payment.payment_method && (
                          <div className="flex items-center gap-1">
                            <CreditCard className="h-3 w-3" />
                            <span>{payment.payment_method}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="text-right ml-4">
                      <div className="flex items-center gap-1 text-lg font-bold text-gray-900">
                        <DollarSign className="h-4 w-4" />
                        <span>{((payment.amount || 0) / 100).toFixed(2)}</span>
                      </div>
                      {payment.credits_purchased && (
                        <p className="text-xs text-gray-500 mt-1">
                          +{payment.credits_purchased} credits
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentHistoryModal;
